import UnleashService from '@/services/unleash'
import sentry from '@/3rd-parties/sentry'
import UnleashFlagTurnOnTime from '@/constants/UnleashFlagTurnOnTime'

const { unleashClient } = UnleashService

const _state = {
  features: {}, // Store feature toggles
  isInitialized: false, // Track whether the client has initialized
  loading: {
    initialization: false,
    contextUpdate: false,
  },
  contextUpdateCounter: 0, // Counter for contextUpdate operations
  context: {},
}

const mutations = {
  setFeatures(state, features) {
    const featureMap = features.reduce((map, feature) => {
      map[feature.name] = feature
      return map
    }, {})

    state.features = featureMap
  },
  setInitialized(state, initialized) {
    state.isInitialized = initialized
  },
  setLoading(state, { type, loading }) {
    if (type === 'contextUpdate') {
      if (loading) {
        state.contextUpdateCounter += 1
      } else {
        state.contextUpdateCounter = Math.max(0, state.contextUpdateCounter - 1)
      }

      state.loading.contextUpdate = state.contextUpdateCounter > 0
    } else {
      state.loading[type] = loading
    }
  },
  setContext(state, context) {
    state.context = {
      ...state.context,
      ...context,
    }
  },
}

const actions = {
  async initializeFeatureToggles({ commit }) {
    commit('setLoading', { type: 'initialization', loading: true })

    try {
      // Handle the ready event to fetch features
      unleashClient.on('ready', () => {
        const features = unleashClient.getAllToggles()
        commit('setFeatures', features)
        commit('setInitialized', true)
        commit('setLoading', { type: 'initialization', loading: false })
      })
    } catch (error) {
      commit('setLoading', false)
      sentry.captureException(error)
    } finally {
      commit('setLoading', { type: 'initialization', loading: false })
    }
  },
  async updateFeatureContext({ state, commit }, context) {
    commit('setLoading', { type: 'contextUpdate', loading: true })

    commit('setContext', context)

    try {
      if (unleashClient) {
        await unleashClient.updateContext(state.context)
        const features = unleashClient.getAllToggles()

        commit('setFeatures', features)
      }
    } catch (error) {
      sentry.captureException(error)
    } finally {
      commit('setLoading', { type: 'contextUpdate', loading: false })
      if (context.onReady) {
        context.onReady()
      }
    }
  },
}

const _getters = {
  isFeatureEnabled: (state) => (featureName) => {
    return state.features[featureName] && state.features[featureName].enabled
  },
  getVariant: (state) => (featureName) => {
    return state.features[featureName] ? state.features[featureName].variant : {
      name: 'disabled',
      enabled: false,
      payload: null,
    }
  },
  isUnleashInitialized: (state) => {
    return state.isInitialized
  },
  isLoadingFeatures: (state) => {
    return state.loading.initialization || state.loading.contextUpdate
  },
  isPresentationCreatedAfterTurnOnFlag: (_, __, rootState) => {
    return (flag) => {
      if (!rootState.presentation || !flag || !UnleashFlagTurnOnTime[flag]) return false
      const flagTurnOnTime = UnleashFlagTurnOnTime[flag]
      return new Date(rootState.presentation.createdAt) > flagTurnOnTime
    }
  },

  canSubscribeMqttChangeSlideEvent: (_, getters, rootState) => {
    return getters.isFeatureEnabled('AHA-30192-presenter-change-slide') && !rootState.presentation.audiencePacing
  },
  canUseNewChangeSlideMechanism: (_, getters, rootState) => {
    return getters.isFeatureEnabled('AHA-30192-presenter-change-slide') && !rootState.presentation.audiencePacing
  },
  canSelfPaceUseNewChangeSlideMechanism: (_, getters) => {
    return getters.isFeatureEnabled('AHA-30192-presenter-change-slide')
  },
  // khi enable flag này lên thì sẽ tắt tính năng Q&A, quiz streak,
  // everyone has answered để đảm bảo số lượng lớn audiences tham gia
  enableForBigEvent: (_, getters) => {
    return getters.isFeatureEnabled('enable-for-big-event')
  },
  turnOffReactionSaveDB: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39736-off-save-reactions-to-db')
  },
  disableUpdateQuizStatusFromQuestionScreenToLobbyScreen: (_, getters) => {
    return getters.isFeatureEnabled('disable-update-quiz-status-from-question-screen-to-lobby-screen')
  },
  canUseNewColors: (_, getters) => {
    return getters.isFeatureEnabled('release-ahaslides-color-palette')
  },
  canUseEveryoneHasAnswered: (_, __, ___, rootGetters) => {
    return rootGetters['liveStatsV2/canUseLiveStatsForQuizV2']
  },

  canUseLogFile: (_, getters) => {
    return getters.isFeatureEnabled('AHA-35479-setup-logging-lib')
  },

  canUseEncryptedSlideData: (_, getters) => {
    return getters.isFeatureEnabled('AHA-38423-encrypt-slide-data')
  },
  // Device Id terminology are often surfaced to the public eyes as universal audience id to avoid controversies,
  // but essentially that's the same as device id and that's how we're gonna address it internally.
  canUseDeviceId: (_, getters) => {
    return getters.isFeatureEnabled('universal-audience-id')
  },
  canUseCookieToLinkAudiences: (_, getters) => {
    return getters.isFeatureEnabled('can-use-cookie-to-link-audiences')
  },
  shouldPushDeviceAssociations: (_, getters) => {
    return getters.isFeatureEnabled('push-audience-id-associations')
  },
  shouldPushCookieAssociations: (_, getters) => {
    return getters.isFeatureEnabled('push-audience-id-cookie-associations')
  },
  canUseXmasDecoration: (_, getters) => {
    return getters.isFeatureEnabled('kill-switch-xmas-decoration')
  },
  canFixSubmitQuizIssue: (_, getters) => {
    return getters.isFeatureEnabled('fix-quiz-submit-button')
  },
  canCheckQuizSlideId: (_, getters) => {
    return getters.isFeatureEnabled('release-check-quiz-slide')
  },
  canUseSkipSlide: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39045-skip-slide')
  },
  canUseFixMqxMissMessageSubmitQuiz: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39885-audience-fix-mqx-not-response-quiz-answer')
  },
  canUseSlidePlugin: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39386-aha-slide-plugin')
  },
  canFixFlickerOnReload: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40971-audience-flicker-reload')
  },
}

export const isFeatureEnabled = (rootGetters, featureName) => {
  return rootGetters['featureToggles/isFeatureEnabled'](featureName)
}

export const isPresentationCreatedAfterTurnOnFlag = (rootGetters, flag) => {
  return rootGetters['featureToggles/isPresentationCreatedAfterTurnOnFlag'](flag)
}

export const isPresentationResetAfterTurnOnFlag = (presentation, flag) => {
  if (!presentation ||
    !presentation.resetTimeStamp ||
    !flag ||
    !UnleashFlagTurnOnTime[flag]
  ) {
    return false
  }
  // resetTimeStamp is a string, so need to convert it to number before parsing to Date object
  return new Date(Number(presentation.resetTimeStamp)) > UnleashFlagTurnOnTime[flag]
}

export default {
  namespaced: true,
  state: _state,
  mutations,
  actions,
  getters: _getters,
}
